import uniqBy from 'lodash/uniqBy';
import moment from 'moment';

/**
 * Grab the minimum and maximum hours for a list of lists of events
 * @param items a LIST OF LISTS
 * @param paddingHoursAfter padding after the last event (in hours)
 * @param paddingHoursAfter padding before the first event (in hours)
 * @returns {(number)[]}
 */
export function grabMinimumStartAndEndHour(items, paddingHoursBefore, paddingHoursAfter) {
    let minHour = 24;
    let maxHour = 0;

    if (items) {
        for (let i = 0; i < items.length; i++) {
            for (let j = 0; j < items[i].length; j++) {
                // format is always this: 2022-03-03 12:00:00
                const hourOfStart = parseInt(items[i][j].event_start.substr(11, 2), 10);
                const hourOfEnd = parseInt(items[i][j].event_end.substr(11, 2), 10);
                minHour = Math.min(minHour, hourOfStart, hourOfEnd);
                maxHour = Math.max(maxHour, hourOfStart, hourOfEnd);
            }
        }
    }
    // sanity check
    if (minHour >= maxHour) {
        maxHour = minHour;
    }
    return [Math.max(0, minHour - paddingHoursBefore), Math.min(24, maxHour + paddingHoursAfter)];
}

export const getCalendarTitle = (start, end) => {
    if (start.year() !== end.year()) {
        return `${start.format('MMM DD, yyyy')} - ${end.format('MMM DD, yyyy')}`;
    }
    if (start.month() !== end.month()) {
        return `${start.format('MMM DD')} - ${end.format('MMM DD, yyyy')}`;
    }
    return `${start.format('MMM DD')} - ${end.format('DD, yyyy')}`;
};

export const uniqueTickets = bookingItems => {
    return uniqBy(bookingItems, e => `${e.activity_start},${e.activity_end}`);
};

function dateToMinutesSinceMidnight(dateStr) {
    return parseInt(dateStr.substr(11, 2)) * 60 + parseInt(dateStr.substr(14, 2));
}

/**
 * Create an array with numbers from start to end, incrementing items by 1
 * @param start
 * @param end
 * @returns {*[]}
 */
function createStartEndArray(start, end) {
    return [...Array(end - start).keys()].map(e => e + start);
}

// just in case, limit the max lanes
const MAX_LANE_INTERATION = 100;

/**
 * Calculate the swim lanes, do the tetris
 * @param events
 * @returns {*}
 */
function calculateSwimLanes(events) {
    // used space is a matrix in a list for
    // we have 24 hours + n lanes, so n * 100 + hours is a good mapping
    const usedSpace = [];
    // the lane count (max index + 1)
    let laneCount = 0;
    for (let i = 0; i < events.length; i++) {
        // if you want to leave some space between before or after, modify these
        const startHour = Math.floor(events[i].event_start_inday / 60);
        const endHour = Math.ceil(events[i].event_end_inday / 60);

        // bruteforce out a lane
        for (let lane = 0; lane < MAX_LANE_INTERATION; lane++) {
            // simple key list for intersect
            const spaceIndexes = createStartEndArray(startHour, endHour).map(
                hour => lane * 100 + hour
            );
            // every space is unused, take it!
            if (!spaceIndexes.some(idx => usedSpace.includes(idx))) {
                usedSpace.push(...spaceIndexes);
                laneCount = Math.max(lane + 1, laneCount);
                events[i].event_swimlane_idx = lane;
                break;
            }
        }
    }
    return events.map(e => ({
        ...e,
        event_swimlane_count: laneCount
    }));
}

export const mapBookingsToDays = (items, startDate, weekDays) => {
    const eventsByDay = [];
    const dayStrings = [];

    // precalculate dayStrings and init eventsByDay
    const weekStart = startDate.clone();
    for (let i = 0; i < weekDays; i++) {
        dayStrings.push(weekStart.format('YYYY-MM-DD'));
        eventsByDay.push([]);
        weekStart.add(1, 'days');
    }

    for (let i = 0; i < items.length; i++) {
        const event = items[i];

        // fix "event to small"
        const activity_start = event.activity_start;
        let activity_end = event.activity_end;
        if (activity_start === activity_end) {
            activity_end = moment(activity_start)
                .clone()
                .add(1, 'hour')
                .format('YYYY-MM-DD HH:mm:ss');
        }

        // calculate start and end day
        const start = activity_start.substr(0, 10);
        const end = activity_end.substr(0, 10);

        for (let j = 0; j < weekDays; j++) {
            // check if an activity fits a date
            const day = dayStrings[j];
            if (start <= day && end >= day) {
                const transformedEvent = {
                    ...event,
                    // if same day, use activity_start, if different day, default to 00:00:00
                    event_start: start === day ? activity_start : `${day} 00:00:00`,
                    // if same day, use activity_end, if different day, default to 23:59:59
                    event_end: end === day ? activity_end : `${day} 23:59:59`,
                    // index of the day inside the calendar
                    event_day_idx: j
                };
                // calculate the minute position (since midnight)
                transformedEvent.event_start_inday = dateToMinutesSinceMidnight(
                    transformedEvent.event_start
                );
                transformedEvent.event_end_inday = dateToMinutesSinceMidnight(
                    transformedEvent.event_end
                );
                // for the overlapping slots we create smaller sizes
                // this needs recalculation once we have all events for a day
                transformedEvent.event_swimlane_count = 1;
                transformedEvent.event_swimlane_idx = 0;
                if (
                    transformedEvent.event_start === transformedEvent.event_end &&
                    (start < day || end > day)
                ) {
                    // special case where we wanted to extend the event to the next day, but resulted in a 0 long overlap
                } else {
                    eventsByDay[j].push(transformedEvent);
                }
            }
        }
    }

    // sort every day by start_date in events
    for (let i = 0; i < weekDays; i++) {
        eventsByDay[i].sort((a, b) => {
            if (a.activity_start < b.activity_start) {
                return -1;
            }
            if (a.activity_start > b.activity_start) {
                return 1;
            }
            return 0;
        });
        eventsByDay[i] = calculateSwimLanes(eventsByDay[i]);
    }
    return eventsByDay;
};
